/**
 * Feedback Page — Matches Xamarin FeedbackPage.xaml + FeedbackPageViewModel.cs
 *
 * Renders the feedback popup inline as a full-screen overlay.
 * Triggered from sidebar navigation. On close, navigates to dashboard.
 */

import { useState, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useToast } from '@/components/ui';
import { ROUTES } from '@/constants';
import { postSuggestion } from '@/service/appFeedbackService';
import { useAppSelector, selectUser, selectFunctionCodes } from '@/store';

import type { FeedbackRequestDTO } from '@/types';

function sanitizeInput(input: string): string {
  return input.replace(/<[^>]*>/g, '').trim();
}

const COOLDOWN_MS = 30000; // 30 seconds

export function FeedbackPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const user = useAppSelector(selectUser);
  const functionCodes = useAppSelector(selectFunctionCodes);

  const [title, setTitle] = useState('');
  const [comment, setComment] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading'>('idle');
  const lastSubmitRef = useRef<number>(0);

  // Matches Xamarin FeedbackItemChangedCommand — enable Send only when both fields filled
  const isSendEnabled = useMemo(
    () => title.trim().length > 0 && comment.trim().length > 0 && status !== 'loading',
    [title, comment, status]
  );

  const handleCancel = useCallback(() => {
    navigate(ROUTES.dashboard);
  }, [navigate]);

  // Matches Xamarin SendCommandHandler
  const handleSend = useCallback(async () => {
    if (!isSendEnabled) return;

    if (Date.now() - lastSubmitRef.current < COOLDOWN_MS) {
      showToast(t('feedback.pleaseWait'), { type: 'warning' });
      return;
    }

    const functionCode = functionCodes?.suggestions ?? '';
    if (!functionCode) {
      showToast(t('feedback.functionCodeNotAvailable'), { type: 'error' });
      return;
    }

    setStatus('loading');

    try {
      const userId = user?._id ?? '';
      if (!userId) {
        showToast(t('feedback.unableToSend'), { type: 'error' });
        setStatus('idle');
        return;
      }

      const request: FeedbackRequestDTO = {
        user: userId,
        title: sanitizeInput(title),
        description: sanitizeInput(comment),
      };

      const response = await postSuggestion(request, functionCode);

      const safeMessage =
        typeof response.message === 'string' ? response.message.slice(0, 200) : '';

      const isError =
        typeof response.status === 'string' &&
        ['error', 'fail', 'failed'].includes(response.status.toLowerCase());

      if (isError) {
        showToast(safeMessage || t('feedback.failedToSubmit'), { type: 'error' });
      } else {
        lastSubmitRef.current = Date.now();
        const isEnglish = i18n.language === 'en';
        showToast(isEnglish && safeMessage ? safeMessage : t('feedback.submittedSuccess'));
      }

      handleCancel();
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : t('feedback.unableToSend');
      showToast(msg, { type: 'error' });
      setStatus('idle');
    }
  }, [
    isSendEnabled,
    functionCodes,
    user,
    title,
    comment,
    showToast,
    t,
    handleCancel,
    i18n.language,
  ]);

  return (
    <div
      className="absolute inset-0 z-50 flex items-center justify-center"
      role="button"
      tabIndex={0}
      onClick={handleCancel}
      onKeyDown={(e) => {
        if (e.key === 'Escape') handleCancel();
      }}
    >
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        className="w-full flex flex-col rounded-[10px] border-[2px] border-[#8DD8E8] p-[10px] animate-in fade-in zoom-in-95 duration-200"
        style={{
          backgroundColor: '#E8F4F8',
          margin: '10px 15px',
        }}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {/* Content area — flex column to fill container height */}
        <div className="flex-1 flex flex-col overflow-y-auto px-[20px] pt-[20px] pb-[10px]">
          {/* Icon */}
          <div className="flex justify-center mb-[4px]">
            <img
              src="/images/feedback.svg"
              alt="Feedback"
              className="h-[36px] w-[36px]"
              aria-hidden="true"
            />
          </div>

          {/* Title */}
          <h2 className="text-center text-[26px] font-semibold font-poppins text-[#415B6D] mb-[2px]">
            {t('feedback.title')}
          </h2>

          {/* Subtitle */}
          <p className="text-center text-[16px] font-normal font-poppins text-[#415B6D] mb-[16px]">
            {t('feedback.subtitle')}
          </p>

          {/* Form fields — flex-1 so comment textarea fills remaining space */}
          <div className="font-poppins flex-1 flex flex-col">
            {/* Title input */}
            <div className="rounded-md border-[1.5px] border-[#8DD8E8] bg-white p-[2px]">
              <input
                id="feedback-title"
                type="text"
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                }}
                placeholder={t('feedback.titlePlaceholder')}
                maxLength={255}
                disabled={status === 'loading'}
                className="w-full h-[46px] bg-transparent px-[10px] text-[16px] text-[#415B6D] placeholder:text-[#A0B0BC] placeholder:font-poppins focus-visible:outline-none"
              />
            </div>

            {/* Comment — grows to fill remaining space */}
            <div className="rounded-lg border-[1.5px] border-[#8DD8E8] bg-white p-[2px] mt-[12px] flex-1 flex flex-col min-h-[150px]">
              <textarea
                id="feedback-comment"
                value={comment}
                onChange={(e) => {
                  setComment(e.target.value);
                }}
                placeholder={t('feedback.commentPlaceholder')}
                maxLength={1000}
                disabled={status === 'loading'}
                className="w-full flex-1 bg-transparent px-[10px] py-[8px] text-[16px] text-[#415B6D] placeholder:text-[#A0B0BC] placeholder:font-poppins focus-visible:outline-none resize-none"
              />
            </div>
          </div>

          {/* Buttons — pinned at bottom */}
          <div className="flex flex-row gap-[20px] justify-end mt-[14px]">
            <button
              type="button"
              className="w-[157px] h-[40px] rounded-[5px] border-[1.5px] border-[#1A3050] bg-transparent text-[12px] text-[#1A3050] font-poppins hover:bg-gray-50"
              onClick={handleCancel}
              disabled={status === 'loading'}
            >
              {t('common.cancel')}
            </button>

            <button
              type="button"
              className={`w-[157px] h-[40px] rounded-[5px] text-[12px] text-white font-poppins ${
                isSendEnabled
                  ? 'bg-[#1A3050] hover:bg-[#0F2540]'
                  : 'bg-[#B0B0B0] cursor-not-allowed'
              }`}
              onClick={() => {
                void handleSend();
              }}
              disabled={!isSendEnabled}
            >
              {status === 'loading' ? t('feedback.sending') : t('feedback.send')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
